import React, { useMemo, useState } from "react";
import axios from "axios";
import AuthModal from "components/Auth/AuthModal";
import SaveCollectionsModal from "components/Collections/SaveCollectionsModal";
import {
  useEntitySaveState,
  useToggleSavedEntityMutation,
} from "components/Collections/useCollections";
import { Bookmark, BookmarkCheck } from "lucide-react";
import "./Save.css";

interface Props {
    entityId: number;
    entityType: string;
    currentUser: { id: number } | null;
    error?: string;
    onSave?: (action?: 'save' | 'unsave') => void;
    className?: string;
}

const Save = ({ entityId, entityType, currentUser, onSave, className }: Props) => {
    const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
    const [showTransitionIcon, setShowTransitionIcon] = useState<boolean>(false);
    const [showCollectionsModal, setShowCollectionsModal] = useState<boolean>(false);
    const statusQuery = useEntitySaveState(entityType, entityId, Boolean(currentUser));
    const saveMutation = useToggleSavedEntityMutation(entityType, entityId);

    const isActive = useMemo(
        () => Boolean(currentUser && statusQuery.data?.isActive),
        [currentUser, statusQuery.data?.isActive]
    );
    const isLoading = statusQuery.isLoading || saveMutation.isLoading;

    const handleSave = async () => {
        if (!currentUser) {
            setShowAuthModal(true);
            return;
        }
        if (isActive) {
            setShowCollectionsModal(true);
            return;
        }

        try {
            setShowTransitionIcon(true);
            await saveMutation.save();
            onSave?.("save");
            setShowCollectionsModal(true);
            window.setTimeout(() => {
                setShowTransitionIcon(false);
            }, 500);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                setShowAuthModal(true);
            } else {
                console.error("Error opening save collections flow:", error);
            }
            setShowTransitionIcon(false);
        }
    };

    if (entityType === "comment") {
        return (
            <>
                <button
                    onClick={() => void handleSave()}
                    disabled={isLoading}
                    className={`flex flex-row items-center gap-2 ${className} group`}
                >
                    {showTransitionIcon ? (
                        <BookmarkCheck className="transition-icon size-4 dark:text-eoe-dark-icon-stroke fill-transparent" />
                    ) : (
                        <Bookmark
                            className={`size-4 dark:text-eoe-dark-icon-stroke ${
                                isActive ? "fill-current" : "fill-transparent"
                            }`}
                        />
                    )}
                    <span>{isActive ? "Saved" : "Save"}</span>
                </button>
                {showCollectionsModal ? (
                    <SaveCollectionsModal
                        isOpen={showCollectionsModal}
                        onClose={() => setShowCollectionsModal(false)}
                        entityId={entityId}
                        entityType={entityType}
                        onSavedChange={onSave}
                    />
                ) : null}
                {showAuthModal ? (
                    <AuthModal show={showAuthModal} onClose={() => setShowAuthModal(false)} />
                ) : null}
            </>
        );
    }

    return (
        <>
            <div className="group save-button size-7 rounded-full text-eoe-gray dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover flex justify-center items-center hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover">
                <button
                    onClick={() => void handleSave()}
                    disabled={isLoading}
                    className={`text-eoe-gray rounded-full dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover flex justify-center items-center hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover ${isActive ? "active" : "inactive"}`}
                >
                    {showTransitionIcon ? (
                        <BookmarkCheck className="transition-icon size-5 text-eoe-gray fill-transparent dark:text-eoe-dark-icon-stroke group-hover:bg-gray-300 dark:group-hover:bg-eoe-dark-icon-hover" />
                    ) : (
                        <Bookmark className={`size-5 text-eoe-gray dark:text-eoe-dark-icon-stroke dark:group-hover:text-eoe-dark-icon-stroke-hover flex justify-center items-center ${isActive ? "fill-current" : "fill-transparent"} group-hover:bg-gray-300 dark:group-hover:bg-eoe-dark-icon-hover`} />
                    )}
                </button>
            </div>

            {showCollectionsModal ? (
                <SaveCollectionsModal
                    isOpen={showCollectionsModal}
                    onClose={() => setShowCollectionsModal(false)}
                    entityId={entityId}
                    entityType={entityType}
                    onSavedChange={onSave}
                />
            ) : null}

            {showAuthModal ? (
                <AuthModal show={showAuthModal} onClose={() => setShowAuthModal(false)} />
            ) : null}
        </>
    );
};

export default Save;
